import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getBillingPlans, getBillingServiceTypes, postSubscription, PREPAID_PRODUCTS_QUERY_KEY } from 'queries';
import { parseErrors, Toast } from 'helpers';
import { JsonDataItem } from 'types';

type UseAddSubscriptionParams = {
  customerId?: string;
  enabled?: boolean;
  onClose?: () => void;
};

type AddSubscriptionPayload = {
  plan_ID: number;
  email_invoice: 0 | 1;
  start_date: string;
  comments?: string;
};

const GEEKCOVER_SERVICE_NAME = 'GeekCover';

export const useAddSubscription = ({ customerId, enabled, onClose }: UseAddSubscriptionParams) => {
  const queryClient = useQueryClient();

  const { data: serviceTypes, isLoading: isTypesLoading } = useQuery(['billing-service-types'], getBillingServiceTypes, {
    enabled: !!enabled,
    // Surface the missing-GeekCover case once per fetch (rather than in an effect,
    // which would double-fire under React StrictMode in development).
    onSuccess: (types) => {
      if (!types?.find((type) => type.service_name === GEEKCOVER_SERVICE_NAME)) {
        Toast.show({ description: 'GeekCover service type is not available.', type: Toast.Type.Error });
      }
    },
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  const serviceType = serviceTypes?.find((type) => type.service_name === GEEKCOVER_SERVICE_NAME);

  const { data: plans, isLoading: isPlansLoading } = useQuery(['billing-plans', serviceType?.ID], getBillingPlans, {
    enabled: !!enabled && !!serviceType,
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  // Plans are shown alphabetically (by description) in the Add Subscription dropdown.
  const sortedPlans = plans?.slice().sort((a, b) => a.description.localeCompare(b.description));

  const { mutate, isLoading: isSaving } = useMutation(postSubscription, {
    onSuccess: (response) => {
      if (response?.tc_sent) {
        Toast.show({ description: 'Terms and Conditions has been sent.', type: Toast.Type.Success });
      }
      queryClient.invalidateQueries([PREPAID_PRODUCTS_QUERY_KEY]);
      onClose?.();
    },
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  const addSubscription = (payload: AddSubscriptionPayload) => {
    if (!customerId || !serviceType) return;

    mutate({ customerId, service_type_id: serviceType.ID, ...payload });
  };

  return {
    serviceType,
    plans: sortedPlans,
    isLoading: isTypesLoading || isPlansLoading,
    isSaving,
    addSubscription,
  };
};
